import React, { useEffect, useState, lazy, createElement } from 'react';
import { CheckCircleIcon, FileTextIcon, LayersIcon, ChevronDownIcon, ChevronUpIcon, CodeIcon, ZapIcon, ShieldCheckIcon, SettingsIcon, LayoutTemplateIcon, DatabaseIcon, SearchIcon, GlobeIcon, MailIcon, Image as ImageIcon, RocketIcon, ServerIcon, MapPinIcon, BookOpenIcon, CheckIcon } from 'lucide-react';
export function ProjectDashboardPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'WordPress Bouwgids | Noord Digital';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Stappenplan, plugins en automatiseringstips voor het nabouwen van deze website in WordPress + Elementor.';
  }, []);
  const [openSteps, setOpenSteps] = useState<Record<number, boolean>>({
    1: true,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    7: false,
    8: false,
    9: false,
    10: false
  });
  const toggleStep = (step: number) => {
    setOpenSteps(prev => ({
      ...prev,
      [step]: !prev[step]
    }));
  };
  const mustHavePlugins = [{
    name: 'Elementor Pro',
    icon: LayoutTemplateIcon,
    description: 'Page builder, templates, dynamic content en theme builder. De motor van de visuele opmaak.',
    color: 'text-pink-600',
    bg: 'bg-pink-100'
  }, {
    name: 'Yoast SEO Premium',
    icon: SearchIcon,
    description: 'SEO titles, meta descriptions, breadcrumbs, XML sitemap en automatische redirects.',
    color: 'text-orange-600',
    bg: 'bg-orange-100'
  }, {
    name: 'ACF Pro',
    icon: DatabaseIcon,
    description: "Advanced Custom Fields voor het beheren van data op lokale pagina's, clusters en sectoren.",
    color: 'text-teal-600',
    bg: 'bg-teal-100'
  }, {
    name: 'WP Rocket',
    icon: RocketIcon,
    description: 'Caching, lazy loading en minification voor een razendsnelle website (Core Web Vitals).',
    color: 'text-amber-600',
    bg: 'bg-amber-100'
  }, {
    name: 'Complianz',
    icon: ShieldCheckIcon,
    description: 'Cookie consent banner (AVG/GDPR conform) en automatische cookie scanner.',
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    name: 'Rank Math (Alternatief)',
    icon: ZapIcon,
    description: 'Alternatief voor Yoast. De gratis versie is zeer compleet en heeft schema markup ingebouwd.',
    color: 'text-indigo-600',
    bg: 'bg-indigo-100'
  }, {
    name: 'Redirection',
    icon: GlobeIcon,
    description: 'Beheer 301 redirects en houd 404-fouten bij (niet nodig als je Yoast Premium gebruikt).',
    color: 'text-slate-600',
    bg: 'bg-slate-100'
  }, {
    name: 'UpdraftPlus',
    icon: ServerIcon,
    description: 'Automatische backups naar de cloud (Google Drive, Dropbox) voor maximale veiligheid.',
    color: 'text-cyan-600',
    bg: 'bg-cyan-100'
  }, {
    name: 'Wordfence',
    icon: ShieldCheckIcon,
    description: 'Uitgebreide beveiliging, firewall en malware scanner om hacks te voorkomen.',
    color: 'text-red-600',
    bg: 'bg-red-100'
  }, {
    name: 'Google Site Kit',
    icon: SearchIcon,
    description: 'Koppel Google Analytics en Search Console direct in je WordPress dashboard.',
    color: 'text-green-600',
    bg: 'bg-green-100'
  }];
  const niceToHavePlugins = [{
    name: 'Dynamic.ooo',
    icon: CodeIcon,
    description: 'Breidt Elementor uit met krachtige dynamic content functies, loops en ACF integraties.'
  }, {
    name: 'JetEngine (Crocoblock)',
    icon: LayersIcon,
    description: 'Maak Custom Post Types, dynamic listings en geavanceerde filters zonder code.'
  }, {
    name: 'Schema Pro',
    icon: CodeIcon,
    description: 'Geavanceerde JSON-LD schema markup (LocalBusiness, FAQ, Article) voor betere rich snippets.'
  }, {
    name: 'Imagify / ShortPixel',
    icon: ImageIcon,
    description: 'Automatische beeldoptimalisatie en WebP conversie voor snellere laadtijden.'
  }, {
    name: 'WP Mail SMTP',
    icon: MailIcon,
    description: 'Zorgt ervoor dat e-mails uit contactformulieren betrouwbaar aankomen en niet in spam belanden.'
  }];
  const steps = [{
    id: 1,
    title: 'WordPress installatie & basis',
    items: ['Kies snelle, betrouwbare hosting (bijv. Kinsta, Cloudways of Siteground)', 'Installeer WordPress en activeer een SSL certificaat (HTTPS)', 'Installeer het "Hello Elementor" thema (lichtgewicht, perfect voor Elementor Pro)', 'Installeer en activeer Elementor & Elementor Pro', 'Installeer Yoast SEO Premium (of Rank Math)', 'Installeer Advanced Custom Fields (ACF) Pro', 'Zet de permalinks op "Berichtnaam" (Instellingen > Permalinks)']
  }, {
    id: 2,
    title: 'Global design opzetten in Elementor',
    items: ['Ga naar Elementor > Site Settings', 'Stel Global Colors in: Navy-900 (#0B1D3A), Accent (#2563EB), Accent-hover (#1D4ED8), Surface-muted (#F8FAFC)', 'Stel Global Fonts in: Inter (Google Font) voor headings en body', 'Bouw de Header template in de Elementor Theme Builder (inclusief mobiel menu)', 'Bouw de Footer template in de Elementor Theme Builder', 'Maak een custom 404-pagina template']
  }, {
    id: 3,
    title: 'Homepage bouwen',
    items: ['Maak een nieuwe pagina "Home" en stel deze in als statische voorpagina (Instellingen > Lezen)', 'Bouw de Hero sectie met CTA knoppen', 'Bouw de Problem sectie ("Waarom een nieuwe website?")', 'Bouw de Services sectie (3 uitgelichte diensten)', 'Bouw de Proces sectie (4 stappen)', 'Bouw de Trust/Over ons sectie met de mascotte', 'Voeg een Blog/Kennisbank preview grid toe', 'Eindig met de Final CTA sectie (contactformulier)', 'TIP: Bouw dit als een reguliere Elementor pagina, niet als template']
  }, {
    id: 4,
    title: "Diensten pagina's (4 stuks)",
    items: ['Maak de pagina "Website laten maken"', 'Maak de pagina "SEO voor websites"', 'Maak de pagina "WordPress websites"', 'Maak de pagina "Hosting & onderhoud"', 'TIP: Bouw de eerste pagina helemaal af in Elementor. Sla deze op als "Page Template" en importeer deze op de andere 3 pagina\'s om tijd te besparen. Pas daarna alleen de teksten en iconen aan.']
  }, {
    id: 5,
    title: 'ACF velden & Custom Post Types aanmaken',
    items: ['Gebruik CPT UI (of JetEngine) om Custom Post Types aan te maken: "Sector", "Cluster", en "Locatie"', 'Maak een ACF Veldgroep voor "Sector": sectorName, heroTitle, introText, features (Repeater), faqItems (Repeater)', 'Maak een ACF Veldgroep voor "Cluster": clusterName, sectorParent (Relationship), heroTitle, introText, pricing, faqItems', 'Maak een ACF Veldgroep voor "Locatie": cityName, region, introText, localContext, nearbyLocations (Repeater), faqItems', 'Maak een ACF Veldgroep voor "Kennisbank" (gekoppeld aan standaard Berichten): readTime, articleType']
  }, {
    id: 6,
    title: 'Elementor templates bouwen met ACF',
    items: ['Ga naar Elementor Theme Builder > Single Post', 'Maak een template voor "Sector" en koppel de widgets (Title, Text Editor) dynamisch aan de ACF velden via de Dynamic Tags (het database-icoontje)', 'Maak een template voor "Cluster" en doe hetzelfde', 'Maak een template voor "Locatie"', 'Maak een template voor enkele "Kennisbank" artikelen', 'Zorg dat de Final CTA sectie onderaan elke template staat']
  }, {
    id: 7,
    title: "Lokale SEO pagina's (Automatiseringstip)",
    items: ["Je moet 40 lokale pagina's maken (Webdesign Alkmaar, Webdesign Haarlem, etc.)", 'SEMI-AUTOMATISCH: Maak een CSV bestand met alle 40 steden en hun unieke teksten (introText, localContext)', 'Gebruik een plugin zoals "WP All Import" gecombineerd met de "ACF Add-on"', 'Importeer de CSV. WP All Import maakt automatisch 40 "Locatie" posts aan en vult de ACF velden in', "Omdat je in stap 6 een Elementor Template hebt gemaakt, zien alle 40 pagina's er direct perfect uit!"]
  }, {
    id: 8,
    title: 'Kennisbank & Portfolio',
    items: ['Maak de Kennisbank overzichtspagina (Archive template in Elementor)', 'Voeg de 11+ artikelen toe als standaard WordPress Berichten', 'Maak een Custom Post Type "Portfolio" voor de case studies', 'Voeg ACF velden toe: clientName, resultText, resultNumber, sectorBadge', 'Bouw de Portfolio overzichtspagina met een Elementor Posts/Portfolio widget']
  }, {
    id: 9,
    title: 'SEO, Breadcrumbs & Schema',
    items: ['Stel Yoast SEO (of Rank Math) in', 'Activeer Breadcrumbs in Yoast en voeg de Elementor Breadcrumbs widget toe aan je templates', "Loop alle pagina's na en vul unieke SEO titels en meta descriptions in", 'Zorg dat de FAQ repeaters (ACF) in Elementor worden weergegeven met een FAQ widget die automatisch FAQPage Schema (JSON-LD) genereert', 'Stel LocalBusiness Schema in via Yoast Local of Schema Pro']
  }, {
    id: 10,
    title: 'Lancering & Optimalisatie',
    items: ['Installeer en configureer WP Rocket voor caching en minification', 'Optimaliseer alle afbeeldingen (Imagify/ShortPixel)', 'Test alle contactformulieren (zorg dat WP Mail SMTP goed is ingesteld)', 'Stel Complianz in voor de cookie banner', 'Koppel Google Analytics en Search Console via Google Site Kit', 'Genereer de XML sitemap en dien deze in bij Google Search Console']
  }];
  return <main className="pt-16 lg:pt-[72px] bg-surface-muted min-h-screen pb-24" data-id="element-1677">
      {/* Hero Section */}
      <section className="bg-navy-900 py-16 lg:py-20 relative overflow-hidden" data-id="element-1678">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} data-id="element-1679" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1680">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent-light text-sm font-medium mb-6" data-id="element-1681">
            <CodeIcon className="w-4 h-4" data-id="element-1682" />
            Developer Guide
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6" data-id="element-1683">
            WordPress Bouwgids — Noord Digital
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-3xl leading-relaxed" data-id="element-1684">
            Stappenplan, plugins en automatiseringstips voor het nabouwen van
            deze React-prototype website in een schaalbare WordPress + Elementor
            omgeving.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 space-y-16" data-id="element-1685">
        {/* NIEUW: ACF Velden Referentie */}
        <section className="bg-white rounded-2xl shadow-card border border-accent/20 overflow-hidden" data-id="element-1686">
          <div className="bg-accent/5 border-b border-accent/10 p-6" data-id="element-1687">
            <div className="flex items-center gap-3 mb-2" data-id="element-1688">
              <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1689" />
              <h2 className="text-2xl font-bold text-navy-900" data-id="element-1690">
                ACF Velden Referentie (Kennisbank)
              </h2>
            </div>
            <p className="text-slate-600" data-id="element-1691">
              Gebruik deze exacte <strong data-id="element-1692">Field Names</strong> in Advanced
              Custom Fields (ACF) voor de Kennisbank artikelen. Het{' '}
              <code data-id="element-1693">KennisbankArticleTemplate</code> is hier volledig op
              gebouwd.
            </p>
            <div className="flex items-center gap-6 mt-4 text-sm" data-id="element-1694">
              <div className="flex items-center gap-2" data-id="element-1695">
                <span className="w-3 h-3 rounded-full bg-green-500 inline-block" data-id="element-1696" />
                <span className="text-slate-600" data-id="element-1697">
                  Al aangemaakt in WordPress
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1698">
                <span className="w-3 h-3 rounded-full bg-orange-500 inline-block" data-id="element-1699" />
                <span className="text-slate-600" data-id="element-1700">
                  Nog toevoegen in WordPress
                </span>
              </div>
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1701">
            <div data-id="element-1702">
              <h3 className="font-bold text-navy-900 mb-3 border-b pb-2" data-id="element-1703">
                Basis (Text/Wysiwyg)
              </h3>
              <ul className="space-y-2 text-sm font-mono" data-id="element-1704">
                <li className="flex items-center gap-2" data-id="element-1705">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1706" />
                  <span className="text-green-700" data-id="element-1707">hero_title</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1708">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1709" />
                  <span className="text-green-700" data-id="element-1710">hero_subtitle</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1711">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1712" />
                  <span className="text-green-700" data-id="element-1713">intro_text</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1714">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1715" />
                  <span className="text-green-700" data-id="element-1716">local_context</span>
                </li>
                <li className="flex items-center gap-2 bg-orange-50 rounded-lg px-2 py-1 -mx-2" data-id="element-1717">
                  <span className="w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold" data-id="element-1718">
                    !
                  </span>
                  <span className="text-orange-700 font-semibold" data-id="element-1719">
                    breadcrumb_label
                  </span>
                  <span className="text-[10px] text-orange-500 font-sans" data-id="element-1720">
                    (nog toevoegen)
                  </span>
                </li>
                <li className="flex items-center gap-2 bg-orange-50 rounded-lg px-2 py-1 -mx-2" data-id="element-1721">
                  <span className="w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold" data-id="element-1722">
                    !
                  </span>
                  <span className="text-orange-700 font-semibold" data-id="element-1723">
                    reading_time
                  </span>
                  <span className="text-[10px] text-orange-500 font-sans" data-id="element-1724">
                    (nog toevoegen)
                  </span>
                </li>
              </ul>
            </div>
            <div data-id="element-1725">
              <h3 className="font-bold text-navy-900 mb-3 border-b pb-2" data-id="element-1726">
                Voordelen & Proces (Repeater)
              </h3>
              <ul className="space-y-2 text-sm font-mono" data-id="element-1727">
                <li className="flex items-center gap-2" data-id="element-1728">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1729" />
                  <span className="text-green-700" data-id="element-1730">benefits_title</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1731">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1732" />
                  <span className="text-green-700" data-id="element-1733">benefits_block</span>
                  <span className="text-xs text-slate-400 font-sans" data-id="element-1734">
                    (title, text)
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1735">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1736" />
                  <span className="text-green-700" data-id="element-1737">process_title</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1738">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1739" />
                  <span className="text-green-700" data-id="element-1740">process_block</span>
                  <span className="text-xs text-slate-400 font-sans" data-id="element-1741">
                    (title, text)
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1742">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1743" />
                  <span className="text-green-700" data-id="element-1744">case_section_title</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1745">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1746" />
                  <span className="text-green-700" data-id="element-1747">case_section_text</span>
                </li>
              </ul>
            </div>
            <div data-id="element-1748">
              <h3 className="font-bold text-navy-900 mb-3 border-b pb-2" data-id="element-1749">
                FAQ & CTA (Text)
              </h3>
              <ul className="space-y-2 text-sm font-mono" data-id="element-1750">
                <li className="flex items-center gap-2" data-id="element-1751">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1752" />
                  <span className="text-green-700" data-id="element-1753">faq_intro</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1754">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1755" />
                  <span className="text-green-700" data-id="element-1756">faq_q1 t/m faq_q5</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1757">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1758" />
                  <span className="text-green-700" data-id="element-1759">faq_a1 t/m faq_a5</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1760">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1761" />
                  <span className="text-green-700" data-id="element-1762">cta_title</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1763">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1764" />
                  <span className="text-green-700" data-id="element-1765">cta_text</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1766">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1767" />
                  <span className="text-green-700" data-id="element-1768">cta_button_text</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1769">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1770" />
                  <span className="text-green-700" data-id="element-1771">cta_button_link</span>
                </li>
              </ul>
            </div>
            <div data-id="element-1772">
              <h3 className="font-bold text-navy-900 mb-3 border-b pb-2" data-id="element-1773">
                SEO & Links (Repeater)
              </h3>
              <ul className="space-y-2 text-sm font-mono" data-id="element-1774">
                <li className="flex items-center gap-2" data-id="element-1775">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1776" />
                  <span className="text-green-700" data-id="element-1777">seo_title</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1778">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1779" />
                  <span className="text-green-700" data-id="element-1780">meta_description</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1781">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1782" />
                  <span className="text-green-700" data-id="element-1783">schema_type</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1784">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1785" />
                  <span className="text-green-700" data-id="element-1786">schema_json</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1787">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1788" />
                  <span className="text-green-700" data-id="element-1789">
                    internal_links_services
                  </span>
                  <span className="text-xs text-slate-400 font-sans" data-id="element-1790">
                    (label, url)
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1791">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1792" />
                  <span className="text-green-700" data-id="element-1793">internal_links_cities</span>
                  <span className="text-xs text-slate-400 font-sans" data-id="element-1794">
                    (label, url)
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-1795">
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-1796" />
                  <span className="text-green-700" data-id="element-1797">related_articles</span>
                  <span className="text-xs text-slate-400 font-sans" data-id="element-1798">
                    (label, url)
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Summary bar */}
          <div className="border-t border-accent/10 bg-accent/5 px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-id="element-1799">
            <div className="flex items-center gap-6 text-sm" data-id="element-1800">
              <span className="font-semibold text-green-700" data-id="element-1801">
                ✓ 24 velden in WordPress
              </span>
              <span className="font-semibold text-orange-600" data-id="element-1802">
                ! 2 velden nog toevoegen
              </span>
            </div>
            <div className="text-xs text-slate-500" data-id="element-1803">
              Voeg{' '}
              <code className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded" data-id="element-1804">
                breadcrumb_label
              </code>{' '}
              en{' '}
              <code className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded" data-id="element-1805">
                reading_time
              </code>{' '}
              toe als Text velden in je ACF Kennisbank veldgroep
            </div>
          </div>
        </section>

        {/* SECTION 1: Statistieken */}
        <section data-id="element-1806">
          <h2 className="text-2xl font-bold text-navy-900 mb-6 flex items-center gap-2" data-id="element-1807">
            <LayersIcon className="w-6 h-6 text-accent" data-id="element-1808" />
            Omvang van het project
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1809">
            <div className="bg-white p-6 rounded-card shadow-soft border border-navy-50" data-id="element-1810">
              <div className="text-sm text-slate-500 font-medium mb-2" data-id="element-1811">
                Totaal pagina's
              </div>
              <div className="text-3xl font-extrabold text-navy-900 mb-2" data-id="element-1812">
                99+
              </div>
              <p className="text-xs text-slate-500" data-id="element-1813">
                Inclusief alle lokale en cluster pagina's
              </p>
            </div>
            <div className="bg-white p-6 rounded-card shadow-soft border border-navy-50" data-id="element-1814">
              <div className="text-sm text-slate-500 font-medium mb-2" data-id="element-1815">
                Elementor Templates
              </div>
              <div className="text-3xl font-extrabold text-navy-900 mb-2" data-id="element-1816">
                4
              </div>
              <p className="text-xs text-slate-500" data-id="element-1817">
                Sector, Cluster, Lokaal, Kennisbank
              </p>
            </div>
            <div className="bg-white p-6 rounded-card shadow-soft border border-navy-50" data-id="element-1818">
              <div className="text-sm text-slate-500 font-medium mb-2" data-id="element-1819">
                Lokale pagina's
              </div>
              <div className="text-3xl font-extrabold text-navy-900 mb-2" data-id="element-1820">
                40
              </div>
              <p className="text-xs text-slate-500" data-id="element-1821">
                Semi-automatiseerbaar met ACF + WP All Import
              </p>
            </div>
            <div className="bg-white p-6 rounded-card shadow-soft border border-navy-50" data-id="element-1822">
              <div className="text-sm text-slate-500 font-medium mb-2" data-id="element-1823">
                Cluster pagina's
              </div>
              <div className="text-3xl font-extrabold text-navy-900 mb-2" data-id="element-1824">
                25
              </div>
              <p className="text-xs text-slate-500" data-id="element-1825">
                Gekoppeld aan 7 hoofdsectoren
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 2: Aanbevolen Plugins */}
        <section data-id="element-1826">
          <h2 className="text-2xl font-bold text-navy-900 mb-6 flex items-center gap-2" data-id="element-1827">
            <SettingsIcon className="w-6 h-6 text-accent" data-id="element-1828" />
            Aanbevolen Plugins
          </h2>

          <h3 className="text-lg font-semibold text-navy-800 mb-4 mt-8" data-id="element-1829">
            Must-have Plugins (De Basis)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1830">
            {mustHavePlugins.map((plugin, index) => <div key={index} className="bg-white p-6 rounded-card shadow-soft border border-navy-50 flex flex-col" data-id="element-1831">
                <div className="flex items-center gap-4 mb-4" data-id="element-1832">
                  <div className={`w-12 h-12 rounded-xl ${plugin.bg} flex items-center justify-center flex-shrink-0`} data-id="element-1833">
                    <plugin.icon className={`w-6 h-6 ${plugin.color}`} data-id="element-1834" />
                  </div>
                  <h4 className="font-bold text-navy-900 text-lg" data-id="element-1835">
                    {plugin.name}
                  </h4>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed flex-grow" data-id="element-1836">
                  {plugin.description}
                </p>
              </div>)}
          </div>

          <h3 className="text-lg font-semibold text-navy-800 mb-4 mt-12" data-id="element-1837">
            Nice-to-have Plugins (Geavanceerd)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1838">
            {niceToHavePlugins.map((plugin, index) => <div key={index} className="bg-white p-6 rounded-card shadow-soft border border-navy-50 flex flex-col opacity-90" data-id="element-1839">
                <div className="flex items-center gap-3 mb-3" data-id="element-1840">
                  <plugin.icon className="w-5 h-5 text-navy-400" data-id="element-1841" />
                  <h4 className="font-bold text-navy-900" data-id="element-1842">{plugin.name}</h4>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-1843">
                  {plugin.description}
                </p>
              </div>)}
          </div>
        </section>

        {/* SECTION 3: Stappenplan */}
        <section data-id="element-1844">
          <h2 className="text-2xl font-bold text-navy-900 mb-6 flex items-center gap-2" data-id="element-1845">
            <MapPinIcon className="w-6 h-6 text-accent" data-id="element-1846" />
            Stappenplan: Volgorde van bouwen
          </h2>
          <div className="bg-white rounded-2xl shadow-soft border border-navy-50 p-2" data-id="element-1847">
            {steps.map(step => <div key={step.id} className="border-b border-navy-50 last:border-0" data-id="element-1848">
                <button onClick={() => toggleStep(step.id)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none hover:bg-navy-50/50 transition-colors rounded-xl" data-id="element-1849">
                  <div className="flex items-center gap-4" data-id="element-1850">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${openSteps[step.id] ? 'bg-accent text-white' : 'bg-navy-100 text-navy-600'}`} data-id="element-1851">
                      {step.id}
                    </div>
                    <span className={`font-bold text-lg ${openSteps[step.id] ? 'text-accent' : 'text-navy-900'}`} data-id="element-1852">
                      {step.title}
                    </span>
                  </div>
                  {openSteps[step.id] ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1853" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1854" />}
                </button>

                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openSteps[step.id] ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-1855">
                  <div className="px-6 pb-6 pt-2 ml-12" data-id="element-1856">
                    <ul className="space-y-3" data-id="element-1857">
                      {step.items.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-1858">
                          {item.startsWith('TIP:') || item.startsWith('SEMI-AUTOMATISCH:') ? <ZapIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1859" /> : <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-1860" />}
                          <span className={`text-sm leading-relaxed ${item.startsWith('TIP:') || item.startsWith('SEMI-AUTOMATISCH:') ? 'text-amber-700 font-medium bg-amber-50 px-3 py-1.5 rounded-lg inline-block' : 'text-slate-600'}`} data-id="element-1861">
                            {item}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>)}
          </div>
        </section>

        {/* SECTION 4: Automatiseringstip Uitgelicht */}
        <section className="bg-navy-900 rounded-2xl p-8 lg:p-12 relative overflow-hidden shadow-card" data-id="element-1862">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent rounded-full opacity-20 blur-3xl transform translate-x-1/2 -translate-y-1/2" data-id="element-1863" />

          <div className="relative z-10" data-id="element-1864">
            <div className="flex items-center gap-3 mb-4" data-id="element-1865">
              <ZapIcon className="w-8 h-8 text-amber-400" data-id="element-1866" />
              <h2 className="text-2xl lg:text-3xl font-bold text-white" data-id="element-1867">
                Pro Tip: Lokale pagina's automatiseren
              </h2>
            </div>
            <p className="text-navy-200 text-lg mb-6 max-w-3xl leading-relaxed" data-id="element-1868">
              Het handmatig aanmaken van 40 lokale pagina's (Webdesign Alkmaar,
              Webdesign Haarlem, etc.) kost enorm veel tijd. Zo doe je dit
              efficiënt in WordPress:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8" data-id="element-1869">
              <div className="bg-white/10 border border-white/20 rounded-xl p-6 backdrop-blur-sm" data-id="element-1870">
                <div className="text-amber-400 font-bold text-xl mb-2" data-id="element-1871">
                  1. Excel / CSV
                </div>
                <p className="text-navy-100 text-sm" data-id="element-1872">
                  Maak een spreadsheet met kolommen voor Stad, Regio,
                  Intro-tekst en Lokale context. Schrijf (of genereer met AI)
                  unieke teksten per stad.
                </p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-xl p-6 backdrop-blur-sm" data-id="element-1873">
                <div className="text-amber-400 font-bold text-xl mb-2" data-id="element-1874">
                  2. WP All Import
                </div>
                <p className="text-navy-100 text-sm" data-id="element-1875">
                  Gebruik de plugin 'WP All Import' + 'ACF Add-on'. Upload je
                  CSV en map de kolommen naar de juiste ACF velden van je Custom
                  Post Type.
                </p>
              </div>
              <div className="bg-white/10 border border-white/20 rounded-xl p-6 backdrop-blur-sm" data-id="element-1876">
                <div className="text-amber-400 font-bold text-xl mb-2" data-id="element-1877">
                  3. Elementor Template
                </div>
                <p className="text-navy-100 text-sm" data-id="element-1878">
                  Omdat je één Elementor Single Template hebt gemaakt die
                  dynamisch de ACF velden inlaadt, staan er na de import direct
                  40 perfect opgemaakte pagina's live!
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>;
}